<script setup>
import { computed, ref, watch } from 'vue'
import { useBillingStore } from '@/stores/billing'
import ProfileAccountBillingService from '@/services/profile-account-billing.service'
import { successMessage } from '@/utils/notifications'
import { discountSize } from '@/helpers/billing-format'

/**
 * Enter a discount code. It goes through the same two steps as every billing
 * change: the API checks the code and says what it would do (preview), then
 * applies exactly that (commit, with the preview's hash). A code that doesn't
 * replace anything is applied straight away. One that replaces the discount the
 * account already has says so first and waits for a tick, since the old one
 * ends and can't be brought back.
 */
const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
})

const emit = defineEmits(['close'])

const billingStore = useBillingStore()

const code = ref('')
// The preview being reviewed, once there is one to review.
const preview = ref()
const acknowledged = ref(false)
const processing = ref(false)
const error = ref('')

const current = computed(() => billingStore.summary?.discount ?? null)
const reviewing = computed(() => !!preview.value)
const proposed = computed(() => preview.value?.discount ?? null)

function reset() {
    code.value = ''
    preview.value = null
    acknowledged.value = false
    processing.value = false
    error.value = ''
}

watch(
    () => props.isOpen,
    (open) => {
        if (open) reset()
    },
    { immediate: true }
)

function messageFrom(e) {
    return (
        e?.response?.data?.message ?? "We couldn't check that code. Try again."
    )
}

async function commit(hash) {
    const response = await ProfileAccountBillingService.commitChange({
        kind: 'apply_discount',
        discount_code: code.value.trim(),
        acknowledged: acknowledged.value,
        hash,
    })
    await billingStore.applySummary(response.data)

    successMessage('Your discount code is applied.')
    emit('close')
}

async function submit() {
    if (processing.value) return

    const trimmed = code.value.trim()
    if (!trimmed) {
        error.value = 'Enter a discount code.'
        return
    }

    processing.value = true
    error.value = ''

    try {
        if (!reviewing.value) {
            const response = await ProfileAccountBillingService.previewChange({
                kind: 'apply_discount',
                discount_code: trimmed,
            })
            const result = response.data

            if (result.requires_acknowledgement) {
                preview.value = result
                return
            }

            await commit(result.hash)
            return
        }

        await commit(preview.value.hash)
    } catch (e) {
        // The calculation moved on since the preview: show the new one and ask
        // again, instead of applying something the owner hasn't seen.
        const newer = e?.response?.data?.data?.preview
        if (e?.response?.status === 409 && newer) {
            preview.value = newer
            acknowledged.value = false
            error.value =
                'Something changed since you looked. Check the details and confirm again.'
        } else {
            error.value = messageFrom(e)
        }
    } finally {
        processing.value = false
    }
}
</script>

<template>
    <zen-slide-in
        :is-open="isOpen"
        title="Discount code"
        :error="error"
        @close="emit('close')"
    >
        <template #body>
            <div v-if="!reviewing" class="space-y-2">
                <label
                    for="discount-code"
                    class="block text-sm font-medium leading-6 text-taupe-900"
                    >Code</label
                >
                <input
                    id="discount-code"
                    v-model="code"
                    type="text"
                    autocomplete="off"
                    autocapitalize="characters"
                    placeholder="SAVE20"
                    class="block w-full rounded-md border-0 px-3 py-2 uppercase text-taupe-900 shadow-sm ring-1 ring-inset ring-taupe-300 placeholder:text-taupe-400 focus:ring-2 focus:ring-inset focus:ring-sky-600 sm:text-sm sm:leading-6"
                    @keydown.enter.prevent="submit"
                />
                <p class="text-xs text-taupe-500">
                    A discount comes off your next invoice, and the ones after
                    it for as long as the code lasts. It never changes an
                    invoice we've already issued.
                </p>
                <p
                    v-if="current"
                    class="rounded-lg bg-taupe-50 p-3 text-sm text-taupe-700"
                    data-testid="discount-current"
                >
                    You have
                    <span class="font-medium">{{ current.code }}</span>
                    ({{ discountSize(current) }}). A new code replaces it.
                </p>
            </div>

            <div v-else class="space-y-4" data-testid="discount-review">
                <p class="text-sm text-taupe-700">
                    <span class="font-medium text-taupe-900">{{
                        proposed.code
                    }}</span>
                    takes {{ discountSize(proposed)
                    }}<template v-if="proposed.months_remaining">
                        for {{ proposed.months_remaining }}
                        {{
                            proposed.months_remaining === 1 ? 'month' : 'months'
                        }}</template
                    >.
                </p>
                <ul
                    v-if="preview.notices?.length"
                    class="list-disc space-y-1 pl-5 text-sm text-taupe-600"
                >
                    <li v-for="notice in preview.notices" :key="notice">
                        {{ notice }}
                    </li>
                </ul>
                <label
                    class="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                >
                    <input
                        v-model="acknowledged"
                        type="checkbox"
                        class="mt-0.5 h-4 w-4 rounded border-taupe-300 text-sky-600 focus:ring-sky-600"
                        data-testid="discount-acknowledge"
                    />
                    <span
                        >I understand my current discount ends and can't be
                        brought back.</span
                    >
                </label>
            </div>
        </template>

        <template #buttons>
            <zen-button
                type="button"
                variant="secondary"
                @click="emit('close')"
            >
                Cancel
            </zen-button>
            <zen-button
                type="button"
                variant="primary"
                :disabled="reviewing && !acknowledged"
                :processing="processing"
                data-testid="apply-discount"
                @click="submit"
            >
                {{ reviewing ? 'Replace discount' : 'Apply code' }}
            </zen-button>
        </template>
    </zen-slide-in>
</template>
